import { useMemo } from 'react'
import { useReactFlow } from '@xyflow/react'
import { SearchOutlined, PhoneOutlined, MailOutlined, EnvironmentOutlined } from '@ant-design/icons'
import { Switch, Tooltip } from 'antd'
import { Button } from 'uikit/buttons/default'
import { AutoComplete } from 'uikit/autocompletes/default'
import { BusinessEntityTypes, type BusinessEntityType, type NodeOption } from '../types'
import { FIT_VIEW_MAX_ZOOM } from 'uikit/graphs/default'
import { FIT_VIEW_DURATION } from '../constants'
import type { EvidenceLayers } from 'components/network-graph/view-case-graph/evidence-layer-menu'
import styles from './controls.module.scss'

interface GraphHeaderProps {
  nodes?: NodeOption[]
  onNodeSelect?: (nodeId: string) => void
  evidenceLayers?: EvidenceLayers
  availableLayers?: EvidenceLayers
  onEvidenceToggle?: (layer: keyof EvidenceLayers) => void
}

export function GraphHeader({
  nodes = [],
  onNodeSelect,
  evidenceLayers,
  availableLayers,
  onEvidenceToggle,
}: GraphHeaderProps) {
  const { fitView } = useReactFlow()

  const searchOptions = useMemo(() => {
    const displayNameNodeTypes: BusinessEntityType[] = [
      BusinessEntityTypes.IDENTITY,
      BusinessEntityTypes.MANUAL_LEAD,
      BusinessEntityTypes.LOCATION,
      BusinessEntityTypes.PHONE,
    ]
    return nodes.map(node => {
      const useDisplayName = displayNameNodeTypes.includes(node.nodeType)
      const label = useDisplayName ? node.displayName || node.identifier : node.identifier
      return {
        value: label,
        nodeId: node.id,
        searchText: `${node.identifier} ${node.displayName}`.toLowerCase(),
      }
    })
  }, [nodes])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelect = (_value: unknown, option: any) => {
    onNodeSelect?.(option.nodeId)
  }

  const handleClear = () => {
    fitView({ padding: 0.1, duration: FIT_VIEW_DURATION, maxZoom: FIT_VIEW_MAX_ZOOM })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filterOption = (inputValue: string, option: any) => {
    return option?.searchText?.includes(inputValue.toLowerCase())
  }

  return (
    <div className={styles.headerContainer}>
      {evidenceLayers && onEvidenceToggle && (
        <div className={styles.evidenceToggles}>
          <Tooltip title={!availableLayers?.location ? "This case doesn't include any addresses" : undefined}>
            <Switch
              className={styles.evidenceSwitch}
              checked={evidenceLayers.location}
              onChange={() => onEvidenceToggle('location')}
              checkedChildren={<EnvironmentOutlined />}
              unCheckedChildren={<EnvironmentOutlined />}
              disabled={!availableLayers?.location}
            />
          </Tooltip>
          <Tooltip title={!availableLayers?.email ? "This case doesn't include any email addresses" : undefined}>
            <Switch
              className={styles.evidenceSwitch}
              checked={evidenceLayers.email}
              onChange={() => onEvidenceToggle('email')}
              checkedChildren={<MailOutlined />}
              unCheckedChildren={<MailOutlined />}
              disabled={!availableLayers?.email}
            />
          </Tooltip>
          <Tooltip title={!availableLayers?.phone ? "This case doesn't include any phone numbers" : undefined}>
            <Switch
              className={styles.evidenceSwitch}
              checked={evidenceLayers.phone}
              onChange={() => onEvidenceToggle('phone')}
              checkedChildren={<PhoneOutlined />}
              unCheckedChildren={<PhoneOutlined />}
              disabled={!availableLayers?.phone}
            />
          </Tooltip>
        </div>
      )}

      <div className={styles.searchGroup}>
        <AutoComplete
          placeholder='e.g. example.com'
          options={searchOptions}
          onSelect={handleSelect}
          onClear={handleClear}
          filterOption={filterOption}
          className={styles.searchInput}
          allowClear
          notFoundContent='No results'
        />
        <Button type='primary' icon={<SearchOutlined />} className={styles.searchButton} />
      </div>
    </div>
  )
}
